import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { easeGradient } from 'react-native-easing-gradient';
import { WebView } from 'react-native-webview';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import TouchableElement from '@components/common/touchableElement';
import { getNCFile } from '@reducers/machines';
import { getNCFileById } from '@reducers/manufacture';
import {
  isAndroid,
  isWeb,
  loadNCFileStatus,
  navigateBack,
  isPhone,
  getNCFileFromVersions,
  ImageUpload,
} from '../../../helpers';
import { scale as scaleInit, notchTop } from '@components/frame';
import { SmallLoading } from '@components/loading';
import Loading from '@components/loading';
import { font as initialFont } from '@styles/fonts';
import Button from '@styles/buttons';
import { ShowFullNcFileButton } from '@v2/elements/ShowFullNcFileButton/ShowFullNcFileButton';
import Svg from '@styles/icons/svg';

import { sendLog } from '@reducers/dataDogs';
import { buildNCFileLoadErrorLogTitle } from '@reducers/dataDogs/helpers';
import NCTextVirtualized from './components/NCTextVirtualized';
import { generateNCFileURL } from '@reducers/blobStorage';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import {
  IMAGE_UPLOAD_FOOTER_TEXT,
  IMAGE_UPLOAD_HEADER_TEXT,
  IMAGE_UPLOAD_MAIN_TEXT,
  SCREEN_BORDER_RADIUS,
} from '../../v2/pages/common/constants/base.constants';
import { useFocusEffect } from '@react-navigation/native';
import { getBoxShadowStyle } from '@styles/shadow';
import { getNCFileHashAndTransfer, shareNCFile } from './helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { useUploadNcFileVersion } from '@components/ncFileVersions/hooks/useUploadNcFileVersion';

const NCFile = React.memo(
  ({
    C,
    isPreview = true,
    paddingHorizontal = 18,
    backgroundColor = C('Background/Light/Field'),
    selectedItem,
    getNCFile,
    hideGradient = false,
    collapsed: initialCollapsed = false,
    showBack,
    showCollapse,
    collapseButtons,
    font = initialFont,
    getNCFileById,
    sendLog,
    baseColor = C.scheme === 'dark' ? '0, 0, 0' : '255, 255, 255',
    paddingTop,
    generateNCFileURL,
    setContentSize,
    hideShowFullButton,
    machines,
    shouldTakeNCFileDataFromMachine = false,
    ncFileData: { data: ncFileData },
    ncFileId: _ncFileId,
    ncFileName: _ncFileName,
    oldScreen,
    isOperatorPanel,
    shouldTruncate = false,
    shouldScroll = true,
    truncateLines,
    setNcFileName,
    setShouldShowGlobalFullNCFileButton,
    isNcFileContentAvailable,
    setIsNcFileContentAvailable,
    showNcFileButtonCallBackOnPress,
    ncFileTextColor,
    singleColor,
    borderTop = false,
    borderRadiusTop = true,
    borderRadiusBottom = false,
    containerPaddingBottom = 10,
    withShadow = false,
    scale = scaleInit,
    selectedVersion,
    onlyNCFileContent = false,
    errorTextStyles,
    ncFileTextContainerStyle,
    withoutCollapsingFeatureAndStyles = false,
    ncFileContentFontConfig,
  }) => {
    const showHeaderBlock = showCollapse || showBack;
    const s = scale;

    const dispatch = useAppDispatch();

    const { safeIsMachinesScreenCheck: _isMachinesScreen, safeIsDetailsScreen: isDetailsScreen } = useNavigationCheck();
    const { onUploadNewVersion } = useUploadNcFileVersion({ ncFile: ncFileData });

    const bg =
      backgroundColor !== C('Background/Light/Field')
        ? backgroundColor
        : isDetailsScreen && !isWeb
        ? C('Background/Light/Field on Panel')
        : C('Background/Light/Field');

    const isMachinesScreen = oldScreen === true || _isMachinesScreen || isOperatorPanel || shouldTakeNCFileDataFromMachine;
    const isNotMachineScreen = !isMachinesScreen;

    const ncFileId = _ncFileId ?? (isNotMachineScreen && ncFileData ? ncFileData?.ncFileId : selectedItem?.machineStatus?.ncFileId);
    const ncFileName = _ncFileName ?? (isNotMachineScreen && ncFileData ? ncFileData?.ncFileName : (selectedItem?.machineStatus?.ncFile ?? selectedItem?.machineStatus?.ncFileName));
    const isNCFileContentAvailable = isMachinesScreen ? selectedItem?.machineStatus?.isNcFileContentAvailable : ncFileData?.isNcFileContentAvailable;


    const machineWhichRanNcFile = isNotMachineScreen ? machines.find((m) => m.fullName === ncFileData?.lastRanByMachineName) : selectedItem;
    const machineVersionData = getNCFileFromVersions(machineWhichRanNcFile?.machineStatus?.versions);
    const detailsVersionData = isNotMachineScreen ? getNCFileFromVersions(ncFileData?.versions) : null;

    const [showFull, setShowFull] = useState(!isPreview);
    const [ncFileContent, setNcFile] = useState({});
    const [collapsed, setCollapsed] = useState(initialCollapsed);
    const isNcFile = Boolean(ncFileContent?.data);
    const webView = useRef(null);
    const virtualizedRef = useRef(null);
    const [isFileDownloading, setIsFileDownloading] = useState(false);
    const [contentFits, setContentFits] = useState(false);

    const errorLoadNCFile = async (type, details) => {
      await sendLog({ title: buildNCFileLoadErrorLogTitle(ncFileName), data: { errorType: type, details, machine: machineWhichRanNcFile }, status: 'error' });
    };

    const downloadNCFile = async () => {
      setIsFileDownloading(true);
      try {
        await shareNCFile({ generateNCFileURL, selectedItem: machineWhichRanNcFile, getNCFileById, isDetailsScreen: isNotMachineScreen, ncFileData });
      } catch (error) {
        console.error(error);
      } finally {
        setIsFileDownloading(false);
      }
    };

    if (!shouldScroll) {
      useFocusEffect(
        useCallback(() => {
          if (setNcFileName) {
            setNcFileName(ncFileName);
          }
        }, [ncFileName])
      );

      useFocusEffect(
        useCallback(() => {
          if (setShouldShowGlobalFullNCFileButton) {
            setShouldShowGlobalFullNCFileButton(!showFull && isNcFile && !hideShowFullButton && ncFileContent?.status === loadNCFileStatus.READY);
          }
        }, [showFull, shouldScroll, isNcFile, hideShowFullButton, ncFileContent?.status])
      );
    }

    useEffect(() => {
      (async () => {
        setNcFile({ status: loadNCFileStatus.LOADING, data: '' });
        const ncFileResult = isNotMachineScreen
          ? selectedVersion || getNCFileFromVersions(ncFileData?.versions)
          : await getNCFileHashAndTransfer({ selectedItem: machineWhichRanNcFile, getNCFileById, ncFileId, selectedVersion });

        const { ncFileContentHash: _ncFileContentHash = null, fileContentHash = null, ncFileTransferFromMachineState = null } = ncFileResult || {};

        const ncFileContentHash = isNotMachineScreen ? fileContentHash : _ncFileContentHash;

        if (!ncFileTransferFromMachineState) {
          errorLoadNCFile(loadNCFileStatus.NO_STATUS, { displayMessage: 'NC File not available', meaning: 'ncFileTransferFromMachineState is null' });
          return setNcFile({
            status: loadNCFileStatus.NO_STATUS,
            displayMessage: 'NC File not available',
            data: '',
          });
        }

        if (ncFileTransferFromMachineState == 'InTransfer') {
          errorLoadNCFile(loadNCFileStatus.DOWNLOADING, {
            displayMessage: 'Downloading NC File from machine...',
            meaning: 'Downloading NC File from machine...',
          });
          return setNcFile({
            status: loadNCFileStatus.DOWNLOADING,
            displayMessage: 'Downloading NC File from machine...',
            data: '',
          });
        }

        if (ncFileTransferFromMachineState !== 'Success') {
          const errorMessage = (() => {
            switch (ncFileTransferFromMachineState) {
              case 'TransferNotSupported':
                return 'NC File has not been downloaded from machine because the machine doesn’t support downloading';
              case 'MachineAccessDenied':
                return 'NC File has not been downloaded from machine because of an incorrect user-password combination';
              case 'ErrorDuringStore':
                return 'NC File not available because of an error during storing.';
              case 'ErrorDuringTransfer':
                return 'NC File not available because of an error during transfer.';
              case 'OtherFailure':
              case 'Unknown':
                return 'NC File not available because of an unknown failure.';
              default:
                return 'NC File not available because of an unknown failure.';
            }
          })();

          errorLoadNCFile(loadNCFileStatus.STATUS_ERROR, { displayMessage: errorMessage, meaning: ncFileTransferFromMachineState });
          return setNcFile({
            status: loadNCFileStatus.STATUS_ERROR,
            displayMessage: errorMessage,
            data: '',
          });
        }

        if (!ncFileContentHash) {
          errorLoadNCFile(loadNCFileStatus.NO_HASH, { displayMessage: 'NC File not available', meaning: 'Content hash is null' });
          return setNcFile({
            status: loadNCFileStatus.NO_HASH,
            displayMessage: 'NC File not available',
            data: '',
          });
        }
        const result = await getNCFile(ncFileContentHash, showFull);
        if (setContentSize && result?.size) {
          setContentSize(result.size);
        }
        if (result.isBlobError) {
          errorLoadNCFile(loadNCFileStatus.BLOB_ERROR, {
            displayMessage: 'NC File not found',
            meaning: "Content hash exists but I can't receive content from blob storage",
          });
          return setNcFile({
            status: loadNCFileStatus.BLOB_ERROR,
            displayMessage: 'NC File not found',
            data: '',
          });
        }
        if (result.isError) {
          errorLoadNCFile(loadNCFileStatus.ERROR, {
            displayMessage: 'NC File not accessible',
            meaning: "Something went wrong and I don't know what",
          });
          return setNcFile({
            status: loadNCFileStatus.ERROR,
            displayMessage: 'NC File not accessible',
            data: '',
          });
        }
        setNcFile({
          status: loadNCFileStatus.READY,
          data: result.data,
        });
      })();
    }, [
      machineWhichRanNcFile?.machineStatus?.ncFileId,
      machineWhichRanNcFile?.machineStatus?.ncFileContentHash,
      machineWhichRanNcFile?.machineStatus?.ncFileTransferFromMachineState,
      machineVersionData?.fileContentHash,
      machineVersionData?.ncFileTransferFromMachineState,
      detailsVersionData?.fileContentHash,
      detailsVersionData?.ncFileTransferFromMachineState,
      showFull,
      isNotMachineScreen,
      selectedVersion?.fileContentHash,
    ]);

    const { colors, locations } = easeGradient({
      colorStops: {
        0: {
          color: `rgba(${baseColor}, 0)`,
        },
        1: {
          color: `rgba(${baseColor}, 0.9)`,
        },
      },
    });

    const { colors: colors2, locations: locations2 } = easeGradient({
      colorStops: {
        0: {
          color: `rgba(${baseColor}, 0.9)`,
        },
        1: {
          color: `rgba(${baseColor}, 1)`,
        },
      },
    });

    const toggleNCFileUpload = () => {
      ImageUpload({
        addingNcFile: true,
        noPhoto: true,
        headerText: IMAGE_UPLOAD_HEADER_TEXT.AddNcFile,
        headerDescription: ncFileName,
        mainText: IMAGE_UPLOAD_MAIN_TEXT.NcFile,
        footerText: IMAGE_UPLOAD_FOOTER_TEXT.AddNcFile,
        documentType: 'NcFile',
        withoutAddExisting: true,
        uploadStart: () => {},
        uploadEnd: () => {},
        tags: [],
      });
    };

    const renderComponent = () => {
      if (ncFileContent?.status === loadNCFileStatus.READY) {
        setIsNcFileContentAvailable?.(true);

        if (!shouldTruncate) {
          return (
            <NCTextVirtualized
              ref={virtualizedRef}
              data={ncFileContent?.data}
              C={C}
              backgroundColor={bg}
              initialCollapsed={initialCollapsed}
              paddingHorizontal={paddingHorizontal}
              scale={scale}
              paddingTop={paddingTop}
              ncFileTextColor={ncFileTextColor}
              singleColor={singleColor}
              borderTop={borderTop}
              borderRadiusTop={borderRadiusTop}
              borderRadiusBottom={borderRadiusBottom}
              withShadow={withShadow}
              containerStyle={ncFileTextContainerStyle}
              withoutCollapsingFeatureAndStyles={withoutCollapsingFeatureAndStyles}
              ncFileContentFontConfig={ncFileContentFontConfig}
            />
          );
        }

        return (
          <NCText
            {...{
              scale,
              data: ncFileContent?.data,
              C,
              backgroundColor: bg,
              isPreview,
              collapsed: initialCollapsed,
              paddingHorizontal,
              showHeaderBlock,
              s,
              webView,
              paddingTop,
              shouldTruncate,
              shouldScroll,
              truncateLines,
              ncFileTextColor,
              singleColor,
              borderTop,
              borderRadiusBottom,
              borderRadiusTop,
              withShadow,
              containerStyle: ncFileTextContainerStyle,
              withoutCollapsingFeatureAndStyles,
              ncFileContentFontConfig,
              setContentFits: shouldScroll ? setContentFits : undefined,
              contentFits,
            }}
          />
        );
      }

      if (
        ncFileContent?.status === loadNCFileStatus.LOADING ||
        (isNcFileContentAvailable && (ncFileContent?.status === undefined || ncFileContent?.status === 'no_status'))
      ) {
        return (
          <View style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <SmallLoading />
          </View>
        );
      }

      return (
        <View style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, width: '100%', paddingHorizontal: s(18) }}>
          <Text
            style={[
              { color: C('Label / Light / Tertiary'), width: '100%', textAlign: 'center', marginTop: s(16), marginBottom: s(12), flexWrap: 'wrap' },
              font.Body,
              errorTextStyles,
            ]}
          >
            {i18n.t(ncFileContent.displayMessage || 'NC File not accessible')}
          </Text>
          {!onlyNCFileContent && isDetailsScreen && (
            <View style={{ maxWidth: s(324), width: s(324), backgroundColor: C('Button/Light/Primary'), borderRadius: s(12) }}>
              <Button
                {...{
                  scale,
                  font,
                  onPress: onUploadNewVersion,
                }}
              >
                <View
                  style={{
                    maxWidth: s(324),
                    height: s(72),
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Svg icon={'uploadCircle'} width={s(23)} height={s(23)} color={C('Button/Light/Primary Text')} />
                  <Text style={[{ color: C('Button/Light/Primary Text'), width: '100%', textAlign: 'center', marginTop: s(8) }, font['Button 1']]}>
                    {i18n.t('Upload NC File')}
                  </Text>
                </View>
              </Button>
            </View>
          )}
        </View>
      );
    };

    const ShowFullNCFileButtonElement = (
      <ShowFullNcFileButton C={C} ncFileId={ncFileId} ncFileName={ncFileName} onPress={showNcFileButtonCallBackOnPress} />
    );

    return (
      <View style={[{ width: '100%', height: '100%' }]}>
        {showHeaderBlock && (
          <View
            style={{
              position: 'absolute',
              width: '100%',
              paddingTop: s(isPhone ? 17 : 39),
              justifyContent: 'space-between',
              paddingHorizontal: s(8),
              zIndex: 1000000,
              flexDirection: 'row',
              backgroundColor: bg,
              paddingBottom: scale(8),
              borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
            }}
          >
            <View />
            <View style={[{ flexDirection: 'row', alignItems: 'center' }]}>
              {showCollapse ? (
                <View style={{ flexDirection: 'row' }}>
                  <TouchableElement
                    onPress={() => {
                      if (virtualizedRef.current) {
                        if (collapsed) {
                          virtualizedRef.current.expandAll();
                        } else {
                          virtualizedRef.current.collapseAll();
                        }
                      } else {
                        webView?.current?.postMessage(collapsed ? 'expand' : 'collapse');
                      }
                      setCollapsed(!collapsed);
                    }}
                  >
                    {({ boxStyle, pressHoverColor }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: s(22),
                            backgroundColor: C(pressHoverColor('Button/Light/PrimaryMono')),
                            borderRadius: s(18),
                          },
                          collapseButtons && {
                            width: s(36),
                            height: s(36),
                            borderRadius: s(36) / 2,
                            justifyContent: 'center',
                            alignItems: 'center',
                          },
                        ]}
                      >
                        {collapseButtons ? (
                          <Svg
                            icon={collapsed ? 'expand' : 'collapse'}
                            width={s(11)}
                            height={s(11)}
                            color="Button/Light/Primary Text"
                            key={collapsed}
                          />
                        ) : (
                          <Text
                            style={[font['Button 1'], { color: C('Button/Light/Mono Icon Text'), paddingHorizontal: s(18), paddingVertical: s(8) }]}
                          >
                            {i18n.t(collapsed ? 'Expand Tools' : 'Collapse Tools')}
                          </Text>
                        )}
                      </View>
                    )}
                  </TouchableElement>
                </View>
              ) : (
                <View />
              )}
              {!isFileDownloading ? (
                <View style={{ flexDirection: 'row' }}>
                  <TouchableElement onPress={downloadNCFile}>
                    {({ boxStyle, pressHoverColor }) => (
                      <View
                        style={[
                          boxStyle,
                          {
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginRight: s(22),
                            width: s(36),
                            height: s(36),
                            borderRadius: s(36) / 2,
                            backgroundColor: C(pressHoverColor('Button/Light/PrimaryMono')),
                            justifyContent: 'center',
                            alignItems: 'center',
                          },
                        ]}
                      >
                        <Svg icon={'download'} width={s(20)} height={s(18)} color="Button/Light/Mono Icon Text" />
                      </View>
                    )}
                  </TouchableElement>
                </View>
              ) : (
                <View
                  style={{
                    marginRight: s(22),
                    marginTop: s(7),
                    width: s(36),
                    height: s(36),
                  }}
                >
                  <SmallLoading />
                </View>
              )}
              {showBack && (
                <TouchableElement onPress={navigateBack}>
                  {({ boxStyle, pressHoverColor }) => (
                    <View
                      style={[
                        boxStyle,
                        {
                          width: s(36),
                          height: s(36),
                          borderRadius: s(36) / 2,
                          backgroundColor: C(pressHoverColor('Button/Light/PrimaryMono')),
                          justifyContent: 'center',
                          alignItems: 'center',
                        },
                      ]}
                    >
                      <Svg icon="close" width={s(13)} height={s(13)} color="Button/Light/Mono Icon Text" />
                    </View>
                  )}
                </TouchableElement>
              )}
            </View>
          </View>
        )}
        <View
          style={[
            {
              width: '100%',
              height: '100%',
              justifyContent: isNcFile ? 'flex-start' : 'center',
              alignItems: isNcFile ? 'flex-start' : 'center',
              paddingHorizontal: showBack ? s(isPhone || isWeb ? 0 : 100) : 0,
              paddingBottom: scale(containerPaddingBottom),
            },
            borderRadiusTop && { borderRadius: s(SCREEN_BORDER_RADIUS) },
          ]}
        >
          {renderComponent()}
        </View>
        {!showFull && shouldScroll && isNcFile && !hideShowFullButton && !contentFits && (
          <View
            style={{
              width: '100%',
              position: 'absolute',
              height: s(74),
              bottom: 0,
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
              borderBottomLeftRadius: s(SCREEN_BORDER_RADIUS),
              borderBottomRightRadius: s(SCREEN_BORDER_RADIUS),
              // overflow: 'hidden',
            }}
          >
            {!hideGradient && (
              <>
                <LinearGradient
                  locations={locations}
                  colors={colors}
                  style={[
                    {
                      position: 'absolute',
                      zIndex: 10,
                      height: s(74) * 0.85,
                      width: '100%',
                    },
                    !shouldScroll &&
                      borderRadiusTop && {
                        borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                        borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                  ]}
                />
                <LinearGradient
                  locations={locations2}
                  colors={colors2}
                  style={[
                    {
                      position: 'absolute',
                      zIndex: 10,
                      height: s(74) * 0.15,
                      bottom: 0,
                      width: '100%',
                    },
                    !shouldScroll &&
                      borderRadiusTop && {
                        borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                        borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                  ]}
                />
              </>
            )}
            {shouldScroll && !onlyNCFileContent && (
              <View style={{ position: 'absolute', zIndex: 100, bottom: s(17) }}>{ShowFullNCFileButtonElement}</View>
            )}
          </View>
        )}
      </View>
    );
  },
  (prev, next) =>
    prev?.selectedItem?.machineStatus?.ncFileId === next?.selectedItem?.machineStatus?.ncFileId &&
    prev.ncFileId === next.ncFileId &&
    prev.ncFileName === next.ncFileName &&
    prev.ncFileData === next.ncFileData &&
    prev?.selectedItem?.machineStatus?.ncFileContentHash === next?.selectedItem?.machineStatus?.ncFileContentHash &&
    prev.font === next.font &&
    prev.C.scheme === next.C.scheme &&
    prev.lg === next.lg &&
    prev.shouldTruncate === next.shouldTruncate &&
    prev.shouldScroll === next.shouldScroll &&
    prev.truncateLines === next.truncateLines &&
    prev.hideShowFullButton === next.hideShowFullButton &&
    prev.isOperatorPanel === next.isOperatorPanel &&
    prev.ncFileTextColor === next.ncFileTextColor &&
    prev.singleColor === next.singleColor &&
    prev.isNcFileContentAvailable === next.isNcFileContentAvailable &&
    prev.isPreview === next.isPreview &&
    prev.backgroundColor === next.backgroundColor &&
    prev.borderRadiusBottom === next.borderRadiusBottom &&
    prev.borderTop === next.borderTop &&
    prev.borderRadiusTop === next.borderRadiusTop &&
    prev.withShadow === next.withShadow &&
    prev.paddingTop === next.paddingTop &&
    prev.selectedVersion?.fileContentHash === next.selectedVersion?.fileContentHash &&
    prev.containerPaddingBottom === next.containerPaddingBottom &&
    prev.shouldTakeNCFileDataFromMachine === next.shouldTakeNCFileDataFromMachine &&
    prev.onlyNCFileContent === next.onlyNCFileContent &&
    prev.errorTextStyles === next.errorTextStyles &&
    prev.ncFileTextContainerStyle === next.ncFileTextContainerStyle &&
    prev.withoutCollapsingFeatureAndStyles === next.withoutCollapsingFeatureAndStyles &&
    prev.ncFileContentFontConfig === next.ncFileContentFontConfig
);

const systemReplace = (s) => (isAndroid ? String(s).replace(/\x00/g, '') : s.replace(/\u0000/g, ''));

const NCText = ({
  data,
  C,
  backgroundColor,
  isPreview,
  collapsed,
  paddingHorizontal,
  showHeaderBlock,
  s,
  webView,
  paddingTop,
  shouldTruncate = false,
  shouldScroll = true,
  truncateLines = 30,
  ncFileTextColor,
  singleColor,
  borderTop,
  borderRadiusTop,
  borderRadiusBottom,
  withShadow,
  containerStyle,
  withoutCollapsingFeatureAndStyles = false,
  scale = scaleInit,
  ncFileContentFontConfig,
  setContentFits,
  contentFits,
}) => {
  const [showLoading, setShowLoading] = useState(true);

  const lines = data.split('\n');
  let blocks = '';

  let lineIndex = 0;
  let needToCloseDiv = false;

  let lineCount = 0;
  let toolsLineCount = 0;

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].replace('\0', '').length === 0) {
      continue; // We don't need to parse empty lines
    }
    if (shouldTruncate && lineCount >= truncateLines) {
      break;
    }

    let line = lines[i];
    let parts = line.split(' ');
    let isCommentStarted = false;

    if (parts) {
      parts.forEach((p, index) => {
        const pFormated = systemReplace(p);

        if (isCommentStarted || pFormated.indexOf(';') > -1) {
          isCommentStarted = true;
          parts[index] = {
            value: p,
            class: 'Comment',
          };
          return;
        }

        if (pFormated.indexOf('FMAX') > -1) {
          parts[index] = {
            value: p,
            class: 'FMAX',
          };
          return;
        }

        if (Number(pFormated) === lineIndex) {
          parts[index] = {
            value: p,
            class: 'LineNumber',
          };
          lineIndex += 1;
          return;
        }

        const isNumber = Number(pFormated);

        if (!isNaN(isNumber)) {
          parts[index] = {
            value: p,
            class: 'Value',
          };
          return;
        }

        const letters = ['X', 'Y', 'Z'];
        const exist = letters.includes(pFormated[0]);
        const numberPart = pFormated.slice(1, p.length);
        const number = Number(numberPart);

        if (exist && numberPart && (number || number === 0)) {
          parts[index] = [
            pFormated[0],
            {
              value: number > 0 ? `+${number}` : number,
              class: 'Value',
            },
          ];
        }
      });
    }

    const renderP = (p, space) => {
      const renderSpace = space ? ' ' : '';
      if (typeof p === 'string') {
        return p + renderSpace;
      }
      if (typeof p === 'object') {
        return `<span class='${p.class}'>${p.value}</span>${renderSpace}`;
      }
      return renderSpace;
    };

    let component = '';

    parts.forEach((part) => {
      if (Array.isArray(part)) {
        part.forEach((p) => {
          component += renderP(p, false);
        });
        component += ' ';
        return;
      }
      component += renderP(part, true);
    });

    const toolCallIndex = line.indexOf('TOOL CALL');
    const commentIndex = line.indexOf(';');
    const isToolCall =
      withoutCollapsingFeatureAndStyles === true ? false : toolCallIndex > -1 && (commentIndex === -1 || commentIndex > toolCallIndex);

    if (isToolCall) {
      toolsLineCount++;
      needToCloseDiv = true;
      const arrowIncon = `
      <svg width="9" height="15" viewBox="0 0 9 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8.54639 7.49805C8.54639 7.11719 8.41455 6.80225 8.09229 6.49463L2.58447 1.104C2.3501 0.869629 2.07178 0.759766 1.74219 0.759766C1.06104 0.759766 0.511719 1.30908 0.511719 1.97559C0.511719 2.31982 0.650879 2.62744 0.907227 2.87646L5.68262 7.50537L0.907227 12.1196C0.650879 12.376 0.511719 12.6836 0.511719 13.0205C0.511719 13.687 1.06104 14.2363 1.74219 14.2363C2.07178 14.2363 2.3501 14.1191 2.58447 13.8921L8.09229 8.50146C8.40723 8.19385 8.54639 7.88623 8.54639 7.49805Z" fill="${C(
          'Button/Light/Primary'
        )}"/>
      </svg>`;
      blocks += `${needToCloseDiv ? '</div></div>' : ''}<div class='main-block'><div class='arrow-icon'>${arrowIncon}</div>`;
    }

    blocks += `<div class='${isToolCall ? 'higlited-line' : ''}'>
    ${isToolCall ? '<span class="toolcall-line">' : ''}${component}${isToolCall ? '</span>' : ''}</div>
    ${isToolCall ? `<div class='tool-block ${collapsed ? '' : 'tool-block-opened'}'>` : ''}`;

    lineCount++;
  }

  if (needToCloseDiv) {
    blocks += '</div></div>';
  }

  useEffect(() => {
    setTimeout(() => {
      setShowLoading(false);
    }, 500);
  }, []);

  const totalHeight = (lineCount - toolsLineCount) * 29 + toolsLineCount * 42;

  const handleContentLayout = (event) => {
    if (setContentFits) {
      const containerHeight = event.nativeEvent.layout.height;
      setContentFits(scale(totalHeight) <= containerHeight);
    }
  };

  return (
    <View onLayout={handleContentLayout} style={[{ width: '100%', height: !shouldScroll ? scale(totalHeight) : '100%' }, containerStyle]}>
      {showLoading && (
        <View
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            position: 'absolute',
            zIndex: 10000,
          }}
        >
          <Loading background={{ backgroundColor }} />
        </View>
      )}
      <WebView
        ref={webView}
        style={[
          borderTop && { borderTopColor: C('Hairline/Light/Primary'), borderTopWidth: StyleSheet.hairlineWidth },
          withShadow && getBoxShadowStyle({ type: 'Field', C }),
          { backgroundColor },
          borderRadiusTop && { borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS), borderTopRightRadius: scale(SCREEN_BORDER_RADIUS) },
          (borderRadiusBottom || (borderRadiusTop && (!shouldScroll || contentFits))) && {
            borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
            borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
          },
        ]}
        key={scale(1)}
        source={{
          html: `
          <!DOCTYPE html>
          <html lang="en">
            <head>
              <meta charset="UTF-8" />
              <meta http-equiv="X-UA-Compatible" content="IE=edge" />
              <meta name="viewport" content="width=device-width, initial-s=1.0" />
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
              <link href="https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap" rel="stylesheet" />

              <style>

                body::-webkit-scrollbar {
                  width: 16px;
                }

                body::-webkit-scrollbar-track {
                  background-color: ${backgroundColor};
                }

                body::-webkit-scrollbar-thumb {
                  background-color: #babac0;
                  border-radius: 16px;
                }

                body::-webkit-scrollbar-button {
                  display: none;
                }

                .search-found-block{
                  position: absolute;
                }

                body{
                  width: 100%;
                  margin: 0;
                  padding: ${paddingTop ? paddingTop : isPreview && !showHeaderBlock ? 0 : notchTop + 18}px 0 ${isPreview ? 0 : 50}px 0;
                  ${!shouldScroll ? 'overflow: hidden;' : ''}
                  overflow-x: hidden;
                }

                .higlited-line{
                  background: ${C('Button/Light/Secondary')};
                  padding: 6.5px 0px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  width: 100%;
                }

                .higlited-line:hover{
                  background: ${C('Button/Light/Secondary hover')};
                }

                .higlited-line:active{
                  background: ${C('Button/Light/Secondary pressed')};
                }

                .toolcall-line{
                  padding-left: ${s(paddingHorizontal)}px;
                  padding-right: 30px
                }

                .main-block{
                  position: relative;
                  width: 100%;
                }

                .tool-block>div{
                  line-height: 0px;
                  opacity: 0;
                  padding: 0 ${s(paddingHorizontal)}px;
                }

                .tool-block-opened div{
                  line-height: ${s(29)}px;
                  opacity: 1;
                }

                .arrow-icon{
                  position: absolute;
                  right: 0;
                  top: 0;
                  width: 42px;
                  height: 42px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  transition: all 0.2s ease-out;
                  cursor: pointer;
                  transform: rotate(${collapsed ? '0' : '90'}deg);
                }
                .Value{
                  color: ${singleColor && ncFileTextColor ? ncFileTextColor : C('NC-Color/Light/Value')}
                }
                .LineNumber{
                  color: ${singleColor && ncFileTextColor ? ncFileTextColor : C('NC-Color/Light/Line number')}
                }
                .Comment{
                  color: ${singleColor && ncFileTextColor ? ncFileTextColor : C('NC-Color/Light/Comment')} !important;
                  word-wrap: break-word;
                }
                .FMAX{
                  color: ${singleColor && ncFileTextColor ? ncFileTextColor : C('NC-Color/Light/FMAX')}
                }
                .wrapper{
                  display: flex;
                  flex-direction: column;
                  padding: 0 ${s(paddingHorizontal)}px;
                  ${
                    !shouldScroll
                      ? `
                      overflow: hidden;
                  `
                      : ''
                  }
                }
                div {
                  font-size: ${ncFileContentFontConfig?.fontSize ?? s(isPhone ? 12 : 16)}px;
                  line-height: ${ncFileContentFontConfig?.lineHeight ?? s(isPhone ? 25 : 29)}px;
                  font-weight: ${ncFileContentFontConfig?.fontWeight ?? 400};
                  letter-spacing: ${ncFileContentFontConfig?.letterSpacing ?? s(isPhone ? 0.15 : 0.3)}px;
                  color: ${singleColor && ncFileTextColor ? ncFileTextColor : C('Label/Light/Primary')};
                  font-family: ${ncFileContentFontConfig?.fontFamily ?? "'Roboto Mono', monospace"};
                };
              </style>
            </head>
            <body>
              <div class="wrapper">${isWeb ? blocks : systemReplace(blocks)}</div>
            </body>
            <script>
              const elementsLines = document.getElementsByClassName('higlited-line') || [];
              const elementsArrows = document.getElementsByClassName('arrow-icon') || [];
              const elements = [...elementsLines, ...elementsArrows]
              for (let i = 0; i < elements.length; i++) {
                const el = elements[i];
                el.addEventListener('click', function (event) {
                  const parentElement = el.parentElement;
                  if (parentElement.querySelector('.tool-block').className.replace(/ /g,'') == 'tool-block') {
                    parentElement.querySelector('.arrow-icon').style.transform = 'rotate(90deg)';
                    parentElement.querySelector('.tool-block').classList.add('tool-block-opened');
                  } else {
                    parentElement.querySelector('.arrow-icon').style.transform = 'rotate(0deg)';
                    parentElement.querySelector('.tool-block').classList.remove('tool-block-opened');
                  }
                });
              }

              window.addEventListener(
                'message',
                function (event) {
                  const type = event.data;

                  if (!type) {
                    return;
                  }

                  for (let i = 0; i < elements.length; i++) {
                    const el = elements[i];

                    const parentElement = el.parentElement;
                    if (type == 'expand') {
                      parentElement.querySelector('.arrow-icon').style.transform = 'rotate(90deg)';
                      parentElement.querySelector('.tool-block').classList.add('tool-block-opened');
                    } else {
                      parentElement.querySelector('.arrow-icon').style.transform = 'rotate(0deg)';
                      parentElement.querySelector('.tool-block').classList.remove('tool-block-opened');
                    }
                  }
                },
                false
              );
            </script>
          </html>
          `,
        }}
      />
    </View>
  );
};

export default connect(({ machines: { machines }, details: { ncFileData } }) => ({ machines, ncFileData }), {
  getNCFile,
  sendLog,
  getNCFileById,
  generateNCFileURL,
})(NCFile);
